import type { Language } from './types';
import { detectLanguage, isLanguage } from './languageDetector';

let currentLanguage: Language = detectLanguage();
const listeners = new Set<() => void>();

const updateDocumentLanguage = () => {
  if (typeof document !== 'undefined') document.documentElement.lang = currentLanguage;
  // Official async-safe Crisp configuration; does not send a chat message.
  // https://docs.crisp.chat/guides/chatbox-sdks/web-sdk/dollar-crisp/#change-locale
  if (typeof window !== 'undefined' && window.$crisp?.push) {
    window.$crisp.push(['config', 'locale', [currentLanguage === 'pt-BR' ? 'pt' : currentLanguage]]);
  }
};
updateDocumentLanguage();

const publish = (lang: Language) => {
  currentLanguage = lang;
  updateDocumentLanguage();
  listeners.forEach(listener => listener());
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('app-language-changed', { detail: lang }));
  }
};

export const setLanguage = (lang: Language) => {
  if (!isLanguage(lang)) return;
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem('app-language', lang);
  } catch {
    // Storage may be disabled; the selection still works for this session.
  }
  publish(lang);
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.key === 'app-language' || event.key === null) {
      publish(isLanguage(event.newValue) ? event.newValue : 'pt-BR');
    }
  });
}

export const getLanguage = (): Language => currentLanguage;
export const subscribeLanguage = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};
export const getAvailableLanguages = (): { code: Language; name: string }[] => [
  { code: 'pt-BR', name: 'Português' },
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
];

/** Authentication resets clear credentials while retaining the UI preference. */
export const clearStoragePreservingLanguage = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.clear();
  try { window.localStorage.setItem('app-language', currentLanguage); } catch {
    // Credentials are already cleared; storage may reject new writes.
  }
};
